import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiSettings, FiLink, FiTrash2 } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { configuracoesAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HelpText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.xs};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$primary) return theme.colors.text.primary;
      return theme.colors.primary[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.primary[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 480px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.primary};
    color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${props => props.$danger ? theme.colors.error[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const removeTrailingSlash = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  let sanitized = value.trim();

  if (!sanitized) {
    return sanitized;
  }

  const protocolIndex = sanitized.indexOf('://');
  const minLength = protocolIndex >= 0 ? protocolIndex + 3 : 0;

  while (sanitized.length > Math.max(minLength, 0) && sanitized.endsWith('/')) {
    sanitized = sanitized.slice(0, -1);
  }

  return sanitized;
};

const ConfiguracaoForm = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const isEditing = !!uuid;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clienteNome, setClienteNome] = useState('');
  
  const [formData, setFormData] = useState({
    id_cliente: '',
    supabase_imagens_base_url: '',
    evolution_api_url: '',
    evolution_api_key: '',
    evolution_instance: ''
  });

  useEffect(() => {
    const carregarClientes = async () => {
      try {
        setLoadingClientes(true);

        const params = {};
        if (!isEditing) {
          params.excluirConfigurados = true;
        } else if (formData.id_cliente) {
          params.excluirConfigurados = true;
          params.clienteAtualId = formData.id_cliente;
        }

        const response = await configuracoesAPI.listarClientes(params);

        if (response.data.success) {
          const clientesDisponiveis = response.data.data || [];
          setClientes(clientesDisponiveis);

          if (!isEditing && clientesDisponiveis.length === 1 && clientesDisponiveis[0]?.id != null) {
            const clienteIdUnico = clientesDisponiveis[0].id.toString();
            setFormData(prev => ({
              ...prev,
              id_cliente: prev.id_cliente || clienteIdUnico
            }));
            setClienteNome(clientesDisponiveis[0].nome_fantasia || clientesDisponiveis[0].nome_social || '');
          }

          if (isEditing && formData.id_cliente) {
            const clienteAtual = clientesDisponiveis.find(
              cliente => cliente.id?.toString() === formData.id_cliente.toString()
            );
            if (clienteAtual) {
              setClienteNome(clienteAtual.nome_fantasia || clienteAtual.nome_social || '');
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
      } finally {
        setLoadingClientes(false);
      }
    };

    if (isEditing && !formData.id_cliente) {
      return;
    }

    carregarClientes();
  }, [isEditing, formData.id_cliente]);

  const hasSingleCliente = !isEditing && clientes.length === 1 && clientes[0]?.id != null;
  const nenhumClienteDisponivel = !isEditing && !loadingClientes && clientes.length === 0;
  const clienteSelecionado = clientes.find(
    cliente => cliente.id?.toString() === formData.id_cliente?.toString()
  );
  const clienteNomeDisplay = clienteNome || clienteSelecionado?.nome_fantasia || clienteSelecionado?.nome_social || '';

  // Carregar dados da configuração se estiver editando
  useEffect(() => {
    if (isEditing && uuid) {
      const carregarConfiguracao = async () => {
        try {
          setLoading(true);
          const response = await configuracoesAPI.buscarPorId(uuid);
          if (response.data.success) {
            const data = response.data.data;
            setFormData({
              id_cliente: data.id_cliente ? data.id_cliente.toString() : '',
              supabase_imagens_base_url: data.supabase_imagens_base_url || '',
              evolution_api_url: data.evolution_api_url || '',
              evolution_api_key: data.evolution_api_key || '',
              evolution_instance: data.evolution_instance || ''
            });
            setClienteNome(data.cliente_nome || data.nome_social || '');
          }
        } catch (error) {
          console.error('Erro ao carregar configuração:', error);
          setError('Erro ao carregar configuração');
        } finally {
          setLoading(false);
        }
      };

      carregarConfiguracao();
    }
  }, [uuid, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'id_cliente') {
      const clienteSelecionado = clientes.find(
        cliente => cliente.id?.toString() === value.toString()
      );
      setClienteNome(clienteSelecionado ? (clienteSelecionado.nome_fantasia || clienteSelecionado.nome_social || '') : '');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (nenhumClienteDisponivel) {
      setError('Todos os clientes disponíveis já possuem configuração cadastrada.');
      return;
    }

    setLoading(true);

    try {
      const sanitizedData = {
        ...formData,
        supabase_imagens_base_url: removeTrailingSlash(formData.supabase_imagens_base_url),
        evolution_api_url: removeTrailingSlash(formData.evolution_api_url)
      };

      let response;
      if (isEditing) {
        response = await configuracoesAPI.atualizar(uuid, sanitizedData);
      } else {
        response = await configuracoesAPI.criar(sanitizedData);
      }

      if (response.data.success) {
        navigate('/configuracoes');
      } else {
        setError(response.data.message || 'Erro ao salvar configuração');
      }
    } catch (err) {
      console.error('Erro ao salvar configuração:', err);
      setError(err.response?.data?.message || 'Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteModal = () => {
    setError(null);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    if (deleting) {
      return;
    }
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!uuid) {
      return;
    }

    setError(null);
    setDeleting(true);

    try {
      const response = await configuracoesAPI.deletar(uuid);

      if (response.data.success) {
        setShowDeleteModal(false);
        navigate('/configuracoes');
      } else {
        setError(response.data.message || 'Erro ao excluir configuração');
      }
    } catch (err) {
      console.error('Erro ao excluir configuração:', err);
      setError(err.response?.data?.message || 'Erro ao excluir configuração');
    } finally {
      setDeleting(false);
    }
  };

  if (loading && isEditing) {
    return (
      <Layout title={isEditing ? 'Editar Configuração' : 'Adicionar Configuração'}>
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <FiLoader style={{ fontSize: '48px', animation: 'spin 1s linear infinite' }} />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar Configuração' : 'Adicionar Configuração'}>
      <Container>
        <Header>
          <BackButton onClick={() => navigate('/configuracoes')}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>{isEditing ? 'Editar Configuração' : 'Adicionar Configuração'}</Title>
        </Header>

        {error && (
          <ErrorMessage>{error}</ErrorMessage>
        )}

        <Form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle>
              <FiSettings />
              Informações Básicas
            </SectionTitle>
            
            <FormGroup>
              <Label>
                Cliente <Required>*</Required>
              </Label>
              <Select
                name="id_cliente"
                value={formData.id_cliente}
                onChange={handleChange}
                required={!isEditing && clientes.length > 0}
                disabled={
                  isEditing ||
                  loading ||
                  hasSingleCliente ||
                  loadingClientes ||
                  nenhumClienteDisponivel
                }
              >
                <option value="">
                  {loadingClientes
                    ? 'Carregando clientes...'
                    : clientes.length === 0
                      ? 'Nenhum cliente disponível'
                      : 'Selecione um cliente'}
                </option>
                {clientes.map(cliente => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nome_fantasia || cliente.nome_social}
                  </option>
                ))}
              </Select>
              {!isEditing && !loadingClientes && clientes.length === 0 && (
                <HelpText style={{ color: theme.colors.error[500], fontWeight: 600 }}>
                  Todos os clientes disponíveis já possuem uma configuração cadastrada.
                </HelpText>
              )}
            </FormGroup>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiLink />
              Supabase - Imagens
            </SectionTitle>
            
            <FormGroup>
              <Label>URL Base de Imagens</Label>
              <Input
                type="url"
                name="supabase_imagens_base_url"
                value={formData.supabase_imagens_base_url}
                onChange={handleChange}
                placeholder="https://exemplo.supabase.co/storage/v1/object/public/..."
                disabled={loading}
              />
              <HelpText>URL base do Supabase Storage para imagens</HelpText>
            </FormGroup>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiSettings />
              Evolution API
            </SectionTitle>
            
            <FormGroup>
              <Label>URL da Evolution API</Label>
              <Input
                type="url"
                name="evolution_api_url"
                value={formData.evolution_api_url}
                onChange={handleChange}
                placeholder="https://api.evolutionapi.com"
                disabled={loading}
              />
              <HelpText>URL base da Evolution API para WhatsApp</HelpText>
            </FormGroup>

            <FormGroup>
              <Label>Chave da API</Label>
              <Input
                type="text"
                name="evolution_api_key"
                value={formData.evolution_api_key}
                onChange={handleChange}
                placeholder="sua-chave-api-aqui"
                disabled={loading}
              />
              <HelpText>Chave de autenticação da Evolution API</HelpText>
            </FormGroup>

            <FormGroup>
              <Label>Nome da Instância</Label>
              <Input
                type="text"
                name="evolution_instance"
                value={formData.evolution_instance}
                onChange={handleChange}
                placeholder="nome-da-instancia"
                disabled={loading}
              />
              <HelpText>Nome da instância do WhatsApp Business</HelpText>
            </FormGroup>
          </FormSection>

          <Actions>
            <Button 
              type="button" 
              onClick={() => navigate('/configuracoes')}
              disabled={loading || deleting}
            >
              Cancelar
            </Button>
            {isEditing && (
              <Button
                type="button"
                onClick={handleOpenDeleteModal}
                $danger
                disabled={loading || deleting}
              >
                <FiTrash2 />
                Excluir
              </Button>
            )}
            <Button 
              type="submit" 
              $primary
              disabled={loading || loadingClientes || nenhumClienteDisponivel}
            >
              {loading ? (
                <>
                  <FiLoader />
                  Salvando...
                </>
              ) : (
                <>
                  <FiSave />
                  Salvar
                </>
              )}
            </Button>
          </Actions>
        </Form>
      </Container>

      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Excluir Configuração</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir esta configuração? Esta ação não pode ser desfeita e irá remover:
              <br /><br />
              - A configuração associada ao cliente <strong>{clienteNomeDisplay || 'selecionado'}</strong>
              <br />
              - Todas as credenciais e parâmetros vinculados
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCancelDelete} disabled={deleting}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleDelete} disabled={deleting}>
                {deleting ? (
                  <>
                    <FiLoader />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <FiTrash2 />
                    Excluir
                  </>
                )}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default ConfiguracaoForm;

